// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { addDryEyeProfile } from "../../../api";
import { AddEditDryEyeType } from "src/services/clinics/types";

export const useAddDryEyeProfile = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (profileData: AddEditDryEyeType) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return addDryEyeProfile(token, clinicId, profileData);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate clinic lens query cache
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dry-eye-profiles", clinicId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Option added successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Option could not be added!"),
      });
    },
  });
};
